import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { Atom, atom } from 'jotai';
import { createContext } from 'react';

import Num, { BigNumber } from '@flipster/utils/num';

export type MilestoneStatus = 'UPCOMING' | 'ONGOING' | 'FINISHED';

export interface MilestoneDataSource {
  statusAtom: Atom<MilestoneStatus>;
  currentMilestoneValueAtom: Atom<BigNumber>;
  maxPrizePoolAtom: Atom<BigNumber>;
  milestonesAtom: Atom<{ prize: BigNumber; value: BigNumber }[]>;
}

export const MilestoneDataSourceContext = createContext<MilestoneDataSource | null>(null);
export const MilestoneDataSourceScope = createScopeFromContext(MilestoneDataSourceContext);

export const milestoneBunja = bunja(() => {
  const dataSource = bunja.use(MilestoneDataSourceScope);

  if (!dataSource) {
    throw new Error('MilestoneDataSourceContext is not provided');
  }

  const { statusAtom, currentMilestoneValueAtom, maxPrizePoolAtom, milestonesAtom } = dataSource;

  const isOngoingAtom = atom((get) => get(statusAtom) === 'ONGOING');
  const isUpcomingAtom = atom((get) => get(statusAtom) === 'UPCOMING');

  const currMilestoneIndexAtom = atom((get) => {
    const currentMilestoneValue = get(currentMilestoneValueAtom);
    const milestones = get(milestonesAtom);
    let index = 0;
    for (let i = 0; i < milestones.length; i += 1) {
      const current = milestones[i];
      if (current.value.lt(currentMilestoneValue)) {
        index = i;
      }
    }
    return index;
  });

  const currentMilestonePrizeAtom = atom((get) => {
    const milestones = get(milestonesAtom);
    const currMilestoneIndex = get(currMilestoneIndexAtom);
    return milestones[currMilestoneIndex]?.prize ?? Num(0);
  });

  const gaugeProgressAtom = atom<number[]>((get) => {
    const milestones = get(milestonesAtom);
    const currentMilestoneValue = get(currentMilestoneValueAtom);

    return milestones.slice(0, -1).map((milestone, index) => {
      const nextMilestone = milestones[index + 1];
      if (currentMilestoneValue.gte(nextMilestone.value)) return 100;
      if (currentMilestoneValue.lte(milestone.value)) return 0;
      return unlerp(milestone.value, nextMilestone.value, currentMilestoneValue);
    });
  });

  const isFinalStageAtom = atom((get) => {
    const milestones = get(milestonesAtom);
    const currMilestoneIndex = get(currMilestoneIndexAtom);
    return currMilestoneIndex >= milestones.length - 3;
  });

  return {
    isOngoingAtom,
    isUpcomingAtom,
    currentMilestonePrizeAtom,
    currentMilestoneValueAtom,
    maxPrizePoolAtom,
    milestonesAtom,
    currMilestoneIndexAtom,
    gaugeProgressAtom,
    isFinalStageAtom,
  };
});
milestoneBunja.debugLabel = 'milestoneBunja';

function unlerp(a: BigNumber, b: BigNumber, v: BigNumber): number {
  return v.minus(a).div(b.minus(a)).multipliedBy(100).toNumber();
}
